import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  Search, 
  User, 
  BookOpen, 
  DollarSign, 
  Plus, 
  Mail, 
  Lock, 
  X, 
  Eye, 
  EyeOff, 
  Shield, 
  UserPlus,
  ShieldCheck,
  ShieldOff,
  Scale,
  Briefcase,
  UserCog
} from 'lucide-react';
import { usersService } from '../services/api';
import { useAuth } from '../../src/context/AuthContext';

const UserTypeBadge = ({ type, isSuperuser, isLegalAdmin, isBusinessAdmin }) => {
  // Check for legal admin first
  if (type === 'admin' && isLegalAdmin) {
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
        <Scale className="h-3 w-3 mr-1" />
        Legal Admin
      </span>
    );
  }
  
  // Check for business admin
  if (type === 'admin' && isBusinessAdmin) {
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
        <Briefcase className="h-3 w-3 mr-1" />
        Business Admin
      </span>
    );
  }
  
  // Super admin
  if (type === 'admin' && isSuperuser) {
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
        <ShieldCheck className="h-3 w-3 mr-1" />
        Super Admin
      </span>
    );
  }
  
  // Regular staff
  if (type === 'admin' && !isSuperuser) {
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
        <Shield className="h-3 w-3 mr-1" />
        Staff
      </span>
    );
  }
  
  // Author
  if (type === 'author') {
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
        <BookOpen className="h-3 w-3 mr-1" />
        Author
      </span>
    );
  }
  
  // Reader
  return (
    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
      <User className="h-3 w-3 mr-1" />
      Reader
    </span>
  );
};

const StaffCreationModal = ({ isOpen, onClose, onCreate, isLoading }) => {
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    first_name: '',
    last_name: '',
    password: '',
    confirmPassword: '',
    admin_type: 'staff', // 'staff', 'legal', 'business'
  });
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});

  const handleSubmit = (e) => {
    e.preventDefault();
    const newErrors = {};

    // Validation
    if (!formData.username.trim()) newErrors.username = 'Username is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    if (!formData.password) newErrors.password = 'Password is required';
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    onCreate(formData);
  };

  const clearForm = () => {
    setFormData({
      username: '',
      email: '',
      first_name: '',
      last_name: '',
      password: '',
      confirmPassword: '',
      admin_type: 'staff',
    });
    setErrors({});
    setShowPassword(false);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center p-4 overflow-y-auto z-50 py-10">
      <div className="bg-white rounded-2xl max-w-lg w-full my-auto">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white rounded-t-2xl">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <UserPlus className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Create Admin Account</h2>
              <p className="text-sm text-gray-600">Add new admin staff member</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            disabled={isLoading}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 overflow-y-auto max-h-[70vh]">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Admin Type *
              </label>
              <div className="grid grid-cols-3 gap-3">
                <button
                  type="button"
                  onClick={() => setFormData({...formData, admin_type: 'staff'})}
                  className={`p-3 rounded-lg border-2 transition-all duration-200 flex flex-col items-center ${
                    formData.admin_type === 'staff'
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <Shield className={`h-5 w-5 mb-1 ${
                    formData.admin_type === 'staff' ? 'text-blue-500' : 'text-gray-400'
                  }`} />
                  <span className={`text-xs font-medium ${
                    formData.admin_type === 'staff' ? 'text-blue-700' : 'text-gray-600'
                  }`}>Staff</span>
                </button>

                <button
                  type="button"
                  onClick={() => setFormData({...formData, admin_type: 'legal'})}
                  className={`p-3 rounded-lg border-2 transition-all duration-200 flex flex-col items-center ${
                    formData.admin_type === 'legal'
                      ? 'border-purple-500 bg-purple-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <Scale className={`h-5 w-5 mb-1 ${
                    formData.admin_type === 'legal' ? 'text-purple-500' : 'text-gray-400'
                  }`} />
                  <span className={`text-xs font-medium ${
                    formData.admin_type === 'legal' ? 'text-purple-700' : 'text-gray-600'
                  }`}>Legal Admin</span>
                </button>

                <button
                  type="button"
                  onClick={() => setFormData({...formData, admin_type: 'business'})}
                  className={`p-3 rounded-lg border-2 transition-all duration-200 flex flex-col items-center ${
                    formData.admin_type === 'business'
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <Briefcase className={`h-5 w-5 mb-1 ${
                    formData.admin_type === 'business' ? 'text-blue-500' : 'text-gray-400'
                  }`} />
                  <span className={`text-xs font-medium ${
                    formData.admin_type === 'business' ? 'text-blue-700' : 'text-gray-600'
                  }`}>Business Admin</span>
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  First Name
                </label>
                <input
                  type="text"
                  value={formData.first_name}
                  onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="John"
                  disabled={isLoading}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Last Name
                </label>
                <input
                  type="text"
                  value={formData.last_name}
                  onChange={(e) => setFormData({...formData, last_name: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Doe"
                  disabled={isLoading}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Username *
                </label>
                <input
                  type="text"
                  value={formData.username}
                  onChange={(e) => setFormData({...formData, username: e.target.value})}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    errors.username ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="johndoe"
                  required
                  disabled={isLoading}
                />
                {errors.username && (
                  <p className="mt-1 text-xs text-red-600">{errors.username}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email *
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className={`w-full pl-10 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      errors.email ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="john@suavemente.com"
                    required
                    disabled={isLoading}
                  />
                </div>
                {errors.email && (
                  <p className="mt-1 text-xs text-red-600">{errors.email}</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Password *
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type={showPassword ? "text" : "password"}
                    value={formData.password}
                    onChange={(e) => setFormData({...formData, password: e.target.value})}
                    className={`w-full pl-10 pr-10 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      errors.password ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="Minimum 8 characters"
                    required
                    disabled={isLoading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    disabled={isLoading}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {errors.password && (
                  <p className="mt-1 text-xs text-red-600">{errors.password}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Confirm Password *
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type={showPassword ? "text" : "password"}
                    value={formData.confirmPassword}
                    onChange={(e) => setFormData({...formData, confirmPassword: e.target.value})}
                    className={`w-full pl-10 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      errors.confirmPassword ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="Confirm password"
                    required
                    disabled={isLoading}
                  />
                </div>
                {errors.confirmPassword && (
                  <p className="mt-1 text-xs text-red-600">{errors.confirmPassword}</p>
                )}
              </div>
            </div>

            <div className="flex items-center justify-between mt-4">
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="flex items-center text-sm text-gray-600 hover:text-gray-800"
                disabled={isLoading}
              >
                {showPassword ? <EyeOff className="h-4 w-4 mr-2" /> : <Eye className="h-4 w-4 mr-2" />}
                {showPassword ? 'Hide Passwords' : 'Show Passwords'}
              </button>
              
              <button
                type="button"
                onClick={clearForm}
                className="text-sm text-red-600 hover:text-red-800"
                disabled={isLoading}
              >
                Clear Form
              </button>
            </div>

            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200 mt-4">
              <div className="flex items-center">
                <Shield className="h-5 w-5 text-blue-600 mr-3 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-gray-900">Admin Permissions</p>
                  <p className="text-xs text-gray-600 mt-1">
                    {formData.admin_type === 'staff' && (
                      <>Staff members have general admin access but cannot access super admin features.</>
                    )}
                    {formData.admin_type === 'legal' && (
                      <>Legal admins can access and manage legal document review requests only.</>
                    )}
                    {formData.admin_type === 'business' && (
                      <>Business admins can access and manage business plan review requests only.</>
                    )}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-end space-x-3 mt-8 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2.5 text-gray-700 bg-gray-100 rounded-lg font-medium hover:bg-gray-200 disabled:opacity-50 transition-colors min-w-[100px]"
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2.5 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 transition-colors min-w-[100px]"
              disabled={isLoading}
            >
              {isLoading ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Creating...
                </div>
              ) : 'Create Account'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default function Users() {
  const { user: currentUser } = useAuth();
  const [filters, setFilters] = useState({
    search: '',
    user_type: ''
  });
  const [showCreateStaffModal, setShowCreateStaffModal] = useState(false);
  const queryClient = useQueryClient();

  const isSuperAdmin = currentUser?.is_superuser;
  const isStaff = currentUser?.user_type === 'admin' && currentUser?.is_staff && !currentUser?.is_superuser;

  // Fetch users - hooks MUST be called at the top level, before any conditional returns
  const { data: users = [], isLoading, error } = useQuery({
    queryKey: ['users', filters],
    queryFn: () => {
      const params = {};
      if (filters.search) params.search = filters.search;
      if (filters.user_type) params.user_type = filters.user_type;
      
      return usersService.getAll(params);
    },
    enabled: isSuperAdmin, // Only fetch if user is super admin
    select: (data) => data || []
  });

  // Create staff mutation
  const createStaffMutation = useMutation({
    mutationFn: async (staffData) => {
      console.log("🚀 Frontend sending data:", staffData);
      
      // Map admin_type to backend fields
      const payload = {
        username: staffData.username,
        email: staffData.email,
        first_name: staffData.first_name || '',
        last_name: staffData.last_name || '',
        password: staffData.password,
        confirmPassword: staffData.confirmPassword,
        user_type: 'admin',
        // Set role flags based on admin_type
        is_staff: true,
        is_superuser: false,
        legal_admin: staffData.admin_type === 'legal',
        business_admin: staffData.admin_type === 'business',
      };
      
      console.log("📤 Mapped payload:", payload);
      const response = await usersService.createStaff(payload);
      console.log("✅ Frontend response:", response);
      return response;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setShowCreateStaffModal(false);
      
      alert(`✅ ${data.message || 'Admin account created successfully!'}`);
    },
    onError: (error) => {
      console.error("❌ Frontend error:", error);
      console.error("❌ Frontend error response:", error.response?.data);
      
      const errorData = error.response?.data;
      
      if (errorData?.errors) {
        const errorMessages = Object.entries(errorData.errors)
          .map(([field, message]) => `${field}: ${message}`)
          .join('\n');
        alert(`Please fix the following errors:\n\n${errorMessages}`);
      } else if (errorData?.error) {
        alert(`Error: ${errorData.error}`);
      } else {
        alert('Failed to create admin account. Please try again.');
      }
    }
  });

  // Delete user mutation
  const deleteUserMutation = useMutation({
    mutationFn: (userId) => usersService.deleteUser(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      alert('User deleted successfully');
    },
    onError: (error) => {
      alert(error.response?.data?.detail || 'Failed to delete user');
    }
  });

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleCreateStaff = async (staffData) => {
    await createStaffMutation.mutateAsync(staffData);
  };

  const handleToggleActive = async (userId, userName, isActive) => {
    const action = isActive ? 'deactivate' : 'activate';
    if (window.confirm(`Are you sure you want to ${action} ${userName}?`)) {
      try {
        const response = await usersService.toggleActive(userId);
        queryClient.invalidateQueries({ queryKey: ['users'] });
        alert(response.message || `User ${action}d successfully!`);
      } catch (error) {
        const errorMsg = error.response?.data?.error || `Failed to ${action} user`;
        alert(`Error: ${errorMsg}`);
      }
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    if (window.confirm(`Are you sure you want to delete ${userName}? This action cannot be undone.`)) {
      await deleteUserMutation.mutateAsync(userId);
    }
  };

  const handlePromoteToAdmin = async (userId, userName) => {
    if (window.confirm(`Promote ${userName} to Super Admin? They will have full access to all features.`)) {
      try {
        const response = await usersService.promoteToAdmin(userId);
        queryClient.invalidateQueries({ queryKey: ['users'] });
        alert(response.message || 'User promoted to Super Admin successfully!');
      } catch (error) {
        const errorMsg = error.response?.data?.error || 'Failed to promote user';
        alert(`Error: ${errorMsg}`);
      }
    }
  };

  const handleDemoteToStaff = async (userId, userName) => {
    if (window.confirm(`Demote ${userName} to Staff? They will have limited access.`)) {
      try {
        const response = await usersService.demoteToStaff(userId);
        queryClient.invalidateQueries({ queryKey: ['users'] });
        alert(response.message || 'Admin demoted to Staff successfully!');
      } catch (error) {
        const errorMsg = error.response?.data?.error || 'Failed to demote user';
        alert(`Error: ${errorMsg}`);
      }
    }
  };

  // If user is staff, show access denied message
  if (isStaff) {
    return (
      <div className="min-h-full flex items-center justify-center p-6">
        <div className="max-w-md w-full text-center">
          <Shield className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-gray-900 mb-2">Access Denied</h3>
          <p className="text-gray-600 mb-6">
            Staff members do not have permission to access the Users Management page.
            This page is restricted to Super Administrators only.
          </p>
        </div>
      </div>
    );
  }

  // If not super admin, don't show the page (this shouldn't happen due to route protection)
  if (!isSuperAdmin) {
    return (
      <div className="min-h-full flex items-center justify-center p-6">
        <div className="max-w-md w-full text-center">
          <Shield className="h-16 w-16 text-red-400 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-gray-900 mb-2">Access Denied</h3>
          <p className="text-gray-600">
            You do not have permission to access this page.
          </p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1d1d1d]"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <div className="text-red-600 mb-4">Error loading users: {error.message}</div>
        <button 
          onClick={() => queryClient.invalidateQueries({ queryKey: ['users'] })}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg"
        >
          Retry
        </button>
      </div>
    );
  }

  const filteredUsers = users.filter(user => {
    if (filters.user_type === 'staff') {
      return user.user_type === 'admin' && !user.is_superuser && !user.legal_admin && !user.business_admin;
    }
    if (filters.user_type === 'legal') {
      return user.legal_admin === true;
    }
    if (filters.user_type === 'business') {
      return user.business_admin === true;
    }
    if (filters.user_type === 'admin') {
      return user.is_superuser;
    }
    if (filters.user_type) {
      return user.user_type === filters.user_type;
    }
    return true;
  });

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Users Management</h1>
          <p className="mt-1 text-sm text-gray-600">
            Manage all platform users, authors, and admin accounts (Super Admin Only)
          </p>
        </div>
        
        {isSuperAdmin && (
          <button
            onClick={() => setShowCreateStaffModal(true)}
            className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2.5 rounded-lg font-medium hover:bg-blue-700 transition-colors"
          >
            <Plus className="h-4 w-4" />
            <span>Create Admin</span>
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="bg-white shadow rounded-lg p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Search
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name, email, or username..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                className="pl-10 w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              User Type
            </label>
            <select
              value={filters.user_type}
              onChange={(e) => handleFilterChange('user_type', e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">All Types</option>
              <option value="author">Authors</option>
              <option value="reader">Readers</option>
              <option value="staff">Staff</option>
              <option value="legal">Legal Admins</option>
              <option value="business">Business Admins</option>
              <option value="admin">Super Admins</option>
            </select>
          </div>

          <div className="flex items-end">
            <button
              onClick={() => setFilters({ search: '', user_type: '' })}
              className="w-full bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  User
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredUsers.map((user) => (
                <tr key={user.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      <div className="h-10 w-10 bg-gray-200 rounded-full flex items-center justify-center">
                        <User className="h-5 w-5 text-gray-400" />
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">
                          {user.first_name && user.last_name 
                            ? `${user.first_name} ${user.last_name}`
                            : user.username
                          }
                        </div>
                        <div className="text-sm text-gray-500">{user.email}</div>
                        {user.momo_number && (
                          <div className="text-xs text-gray-400">
                            {user.momo_number}
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <UserTypeBadge 
                      type={user.user_type} 
                      isSuperuser={user.is_superuser}
                      isLegalAdmin={user.legal_admin}
                      isBusinessAdmin={user.business_admin}
                    />
                  </td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      user.is_active 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {user.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex space-x-8">
                      {isSuperAdmin && user.id !== currentUser?.id && (
                        <>
                          <button
                            onClick={() => handleToggleActive(user.id, user.username, user.is_active)}
                            className={`text-sm font-medium flex items-center ${
                              user.is_active 
                                ? 'text-orange-600 hover:text-orange-800' 
                                : 'text-green-600 hover:text-green-800'
                            }`}
                            title={user.is_active ? 'Deactivate User' : 'Activate User'}
                          >
                            {user.is_active ? 'Deactivate' : 'Activate'}
                          </button>
                          
                          {/* Show promote for staff or legal/business admins */}
                          {(user.user_type === 'admin' && !user.is_superuser) && (
                            <button
                              onClick={() => handlePromoteToAdmin(user.id, user.username)}
                              className="text-sm text-green-600 hover:text-green-800 font-medium flex items-center"
                              title="Promote to Super Admin"
                            >
                              <ShieldCheck className="h-4 w-4 mr-1" />
                              Promote
                            </button>
                          )}
                          
                          {/* Show demote for super admins */}
                          {user.is_superuser && (
                            <button
                              onClick={() => handleDemoteToStaff(user.id, user.username)}
                              className="text-sm text-orange-600 hover:text-orange-800 font-medium flex items-center"
                              title="Demote to Staff"
                            >
                              <ShieldOff className="h-4 w-4 mr-1" />
                              Demote
                            </button>
                          )}
                          
                          <button
                            onClick={() => handleDeleteUser(user.id, user.username)}
                            className="text-sm text-red-600 hover:text-red-800 font-medium"
                          >
                            Delete
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              
              {filteredUsers.length === 0 && (
                <tr>
                  <td colSpan="5" className="px-6 py-12 text-center">
                    <User className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      No users found
                    </h3>
                    <p className="text-gray-600">
                      Try adjusting your search or filter criteria.
                    </p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Staff Creation Modal */}
      {isSuperAdmin && (
        <StaffCreationModal
          isOpen={showCreateStaffModal}
          onClose={() => setShowCreateStaffModal(false)}
          onCreate={handleCreateStaff}
          isLoading={createStaffMutation.isLoading}
        />
      )}
    </div>
  );
}